import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import ServiceCard from "@/components/marketplace/ServiceCard";
import ProviderCard from "@/components/marketplace/ProviderCard";
import TemplateBands from "@/components/marketplace/TemplateBands";
import HotDealsStrip from "@/components/deals/HotDealsStrip";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import api from "@/lib/api";

export default function CategoryDetailPage() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [subCats, setSubCats] = useState([]);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useDocumentTitle(cat?.name || (notFound ? "Category not found" : "Category"));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/categories/${slug}`);
        setCat(data);
        const [subs, s, p] = await Promise.all([
          api.get("/categories", { params: { parent_id: data.id } }),
          api.get("/services", { params: { category_id: data.id, limit: 24 } }),
          api.get("/providers", { params: { category_id: data.id, limit: 12 } }),
        ]);
        setSubCats(subs.data);
        setServices(s.data.items || []);
        setProviders(p.data.items || []);
      } catch (_) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl text-[#071b22]">Category not found.</h1>
          <Link to="/categories" className="mt-6 inline-flex items-center gap-2 text-[#165a71] font-medium"><ArrowLeft className="h-4 w-4" /> All categories</Link>
        </div>
      </PublicLayout>
    );
  }
  if (!cat) return <PublicLayout><div className="mx-auto max-w-3xl px-6 py-24 text-[#252A34]/60">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <TemplateBands entity="categories" slot="header" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="flex items-center gap-2 text-xs text-[#252A34]/60 mb-3">
          <Link to="/categories" className="hover:text-[#165a71]">Categories</Link>
          <span>/</span>
          <span className="capitalize">{cat.kind}</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#071b22] tracking-tight" data-testid="category-title">{cat.name}</h1>
        {cat.description && <p className="mt-3 text-[#252A34]/75 max-w-2xl">{cat.description}</p>}

        {subCats.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-xl text-[#071b22] font-semibold mb-4">Subcategories</h2>
            <div className="flex flex-wrap gap-2" data-testid="category-subs">
              {subCats.map((sc) => (
                <Link key={sc.id} to={`/services?category_id=${sc.id}`} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-[#252A34] hover:border-[#165a71]/40 hover:text-[#165a71] transition-colors">
                  {sc.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        <HotDealsStrip categoryId={cat.id} title={`Hot deals in ${cat.name}`} className="mt-12" />

        <section className="mt-12">
          <h2 className="font-display text-2xl text-[#071b22] font-semibold mb-6">Services in {cat.name}</h2>
          {services.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center bg-white" data-testid="category-services-empty">
              <div className="text-[#252A34]/70">No services published in this category yet.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="category-services">
              {services.map((s, i) => (
                <ServiceCard key={s.id} service={s} index={i} />
              ))}
            </div>
          )}
        </section>

        {providers.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl text-[#071b22] font-semibold mb-6">Providers offering {cat.name}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="category-providers">
              {providers.map((p, i) => (
                <ProviderCard key={p.id} provider={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
      <TemplateBands entity="categories" slot="footer" />
    </PublicLayout>
  );
}
